<script setup lang="ts">
/**
 * Thin styled wrapper over NxDropdown. Like NxDropdown it is fully controlled
 * and has no built-in click-outside dismiss — the consumer owns `isOpen` and
 * must close on outside interaction itself. See NxDropdown for why that policy
 * is left to consumers rather than baked into the primitive.
 */
import { NxDropdown } from '../Dropdown';

withDefaults(
  defineProps<{
    isOpen: boolean;
    align?: 'start' | 'center' | 'end';
    fitWidth?: boolean;
  }>(),
  { align: 'start', fitWidth: true }
);

const emit = defineEmits<{ close: [] }>();
</script>

<template>
  <div class="nx-menu-dropdown">
    <NxDropdown :is-open="isOpen" :align="align" :fit-width="fitWidth" @close="emit('close')">
      <template #trigger>
        <slot name="trigger" />
      </template>
      <div class="nx-menu-panel">
        <slot />
      </div>
    </NxDropdown>
  </div>
</template>

<style scoped>
.nx-menu-dropdown {
  display: inline-flex;
  position: relative;
  /* Content-sized even as a grid or flex-column item: blockified and
     stretched, the anchor took the container's width, so a fit-width menu
     spanned the whole container and an end-aligned one opened far from its
     trigger. */
  width: fit-content;
  max-width: 100%;
}

.nx-menu-panel {
  border-radius: var(--nxt-radius-lg);
  padding: var(--nxt-space-2xs);
  background-color: var(--nxt-surface-raised-bg);
  border: solid var(--nxt-hairline) var(--nxt-border);
  box-shadow: var(--nxt-shadow-lg);
  color: var(--nxt-fg);
  user-select: none;
  gap: calc(var(--nxt-hairline) * 2);

  min-width: 10em;
  max-height: 20em;
  overflow-y: auto;
  overscroll-behavior: contain;

  display: flex;
  flex-direction: column;
}
</style>
